// Installs a global fetch wrapper that:
//   1. Injects `Authorization: Bearer <token>` from localStorage on /api/* requests.
//   2. Pre-buffers the response body so the Emergent platform's PostHog session-
//      replay interceptor (loaded via index.html) can read its copy AND the app
//      code can still call .json()/.text(). Without this, every error response
//      surfaces as "body stream already read".
// Call once at app startup.
let installed = false;

export function installAuthFetch() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const reqUrl = typeof input === 'string' ? input : (input?.url || '');
    const isApiReq = reqUrl.includes('/api/');
    try {
      if (isApiReq) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined));
          if (!headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`);
          }
          init = { ...init, headers };
        }
      }
    } catch (e) {
      // Never break the underlying fetch if header injection fails.
    }

    const response = await originalFetch(input, init);

    // Stale/unknown token → the backend refuses to serve data (401
    // unknown_token) so the wrong environment is never shown. Drop the token
    // and reload for a clean re-login.
    if (isApiReq && response.status === 401 && localStorage.getItem('auth_token')) {
      try {
        const data = await response.clone().json();
        if (data?.detail?.error === 'unknown_token') {
          localStorage.removeItem('auth_token');
          window.location.reload();
          return response;
        }
      } catch (_) { /* non-JSON 401 — fall through */ }
    }

    // Only buffer JSON-ish responses; leave binary streams (PDFs, downloads)
    // untouched so the browser can stream them.
    const ct = response.headers.get('content-type') || '';
    if (!ct || ct.includes('application/json') || ct.startsWith('text/') || ct.includes('xml')) {
      try {
        const buf = await response.clone().arrayBuffer();
        return new Response(buf, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      } catch (_) {
        // If the clone itself fails (someone already drained the original)
        // just return the original response and let callers cope.
        return response;
      }
    }
    return response;
  };
}
